<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import campaignMixin from 'shared/mixins/campaignMixin';
import CampaignsTable from '../CampaignsTable.vue';
import CampaignFilter from './CampaignFilter.vue';
import CustomPagination from 'dashboard/components/table/CustomPagination.vue';

export default {
  components: {
    CampaignsTable,
    CampaignFilter,
    CustomPagination,
  },
  mixins: [campaignMixin],
  props: {
    type: {
      type: String,
      default: '',
    },
  },
  emits: ['changeSort'],
  data() {
    return {
      pageSize: 10,
      pageIndex: 1,
      showEditPopup: false,
      selectedCampaign: {},
      showDeleteConfirmationPopup: false,
      showAbortConfirmationPopup: false,
      selectedStatus: [],
      searchTitle: '',
      searchQuery: '',
      sortFilter: '',
      dateFilter: {
        start_at: '',
        end_at: '',
      },
      organicOrRecurringFilter: 'organic',
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaigns/getUIFlags',
      labelList: 'labels/getLabels',
    }),
    totalCampaignsCount() {
      return this.campaignsResponse?.total_count || 0;
    },
    campaigns() {
      if (!this.campaignsResponse?.campaigns?.length) return [];
      return this.campaignsResponse?.campaigns;
    },
    campaignsResponse() {
      return this.$store.getters['campaigns/getCampaignsResponses'];
    },
    showEmptyResult() {
      return !this.uiFlags.isFetching && this.campaigns.length === 0;
    },
  },
  mounted() {
    this.searchByFilters();
  },
  methods: {
    onPageNumberChange(pageIndex) {
      this.pageIndex = pageIndex;
      this.searchByFilters();
    },
    onChangeFilter(value, type) {
      if (type === 'sort') {
        this.$emit('changeSort', value);
      }
    },
    openEditPopup(campaign) {
      this.selectedCampaign = campaign;
      this.showEditPopup = true;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    openDeletePopup(campaign) {
      this.showDeleteConfirmationPopup = true;
      this.selectedCampaign = campaign;
    },
    openAbortPopup(campaign) {
      this.showAbortConfirmationPopup = true;
      this.selectedCampaign = campaign;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    closeAbortPopup() {
      this.showAbortConfirmationPopup = false;
    },
    confirmDeletion() {
      this.closeDeletePopup();
      const { id } = this.selectedCampaign;
      this.deleteCampaign(id);
    },
    async deleteCampaign(id) {
      try {
        await this.$store.dispatch('campaigns/delete', id);
        useAlert(this.$t('CAMPAIGN.DELETE.API.SUCCESS_MESSAGE'));
        this.searchByFilters();
      } catch (error) {
        useAlert(this.$t('CAMPAIGN.DELETE.API.ERROR_MESSAGE'));
      }
    },
    confirmAbort() {
      this.closeAbortPopup();
      const { id } = this.selectedCampaign;
      this.abortCampaign(id);
    },
    async abortCampaign(id) {
      try {
        await this.$store.dispatch('campaigns/abort', id);
        useAlert(this.$t('CAMPAIGN.ABORT.API.SUCCESS_MESSAGE'));
        this.searchByFilters();
      } catch (error) {
        useAlert(this.$t('CAMPAIGN.ABORT.API.ERROR_MESSAGE'));
      }
    },
    updateSearchTitle(title) {
      this.pageIndex = 1;
      this.searchTitle = title;
      this.searchByFilters();
    },
    updateStatusFilter(statuses) {
      this.pageIndex = 1;
      this.selectedStatus = statuses;
    },
    updateSortFilter(sortValue) {
      this.sortFilter = sortValue;
      this.searchByFilters();
    },
    updateDateFilter({ start_at, end_at }) {
      this.pageIndex = 1;
      this.dateFilter = { start_at, end_at };
    },
    updateOrganicOrRecurringFilter(type) {
      this.pageIndex = 1;
      this.organicOrRecurringFilter = type;
    },

    searchByFilters() {
      const queryParams = {
        status: this.selectedStatus.join(','),
        title: this.searchTitle,
        sort_by: this.sortFilter || 'scheduled_at_desc',
        start_at: this.dateFilter.start_at || '',
        end_at: this.dateFilter.end_at || '',
        page: this.pageIndex,
        origin: this.organicOrRecurringFilter || '',
      };

      this.$store.dispatch('campaigns/get', queryParams);
    },
  },
};
</script>

<template>
  <div class="min-h-[31.25rem] flex-1 overflow-auto">
    <CampaignFilter
      v-model:search-query="searchQuery"
      @search="updateSearchTitle"
      @status-change="updateStatusFilter"
      @change-sort="updateSortFilter"
      @date-filter-change="updateDateFilter"
      @organic-or-recurring-filter-change="updateOrganicOrRecurringFilter"
      @edit-filters="searchByFilters"
    />
    <CampaignsTable
      :campaigns="campaigns"
      :show-empty-result="showEmptyResult"
      :is-loading="uiFlags.isFetching"
      @edit="openEditPopup"
      @delete="openDeletePopup"
      @abort="openAbortPopup"
    />
    <div v-if="totalCampaignsCount">
      <CustomPagination
        class="mt-2"
        :total="totalCampaignsCount"
        :current-page="pageIndex"
        @page-change="onPageNumberChange"
      />
    </div>

    <woot-delete-modal
      v-if="showDeleteConfirmationPopup"
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CAMPAIGN.DELETE.CONFIRM.TITLE')"
      :message="$t('CAMPAIGN.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('CAMPAIGN.DELETE.CONFIRM.YES')"
      :reject-text="$t('CAMPAIGN.DELETE.CONFIRM.NO')"
    />
    <woot-delete-modal
      v-if="showAbortConfirmationPopup"
      v-model:show="showAbortConfirmationPopup"
      :on-close="closeAbortPopup"
      :on-confirm="confirmAbort"
      :title="$t('CAMPAIGN.ABORT.CONFIRM.TITLE')"
      :message="$t('CAMPAIGN.ABORT.CONFIRM.MESSAGE')"
      :confirm-text="$t('CAMPAIGN.ABORT.CONFIRM.YES')"
      :reject-text="$t('CAMPAIGN.ABORT.CONFIRM.NO')"
    />
  </div>
</template>
